
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { RepairLineItem } from '@/types/quick-compare';
import { formatCurrency } from '@/utils/estimates/formatCurrency';

interface ItemCardProps {
  item: RepairLineItem & { section?: string };
  variant: 'added' | 'removed' | 'modified' | 'original';
  showDifference?: boolean;
  originalValue?: number;
}

export const ItemCard: React.FC<ItemCardProps> = ({ 
  item, 
  variant, 
  showDifference = false, 
  originalValue
}) => {
  const bgColors = {
    added: 'bg-green-50 border-green-200',
    removed: 'bg-red-50 border-red-200',
    modified: 'bg-green-50/50',
    original: 'bg-red-50/50',
  };
  
  const badgeColors = {
    added: 'bg-green-100 text-green-700',
    removed: 'bg-red-100 text-red-700',
    modified: 'bg-green-100 text-green-700',
    original: 'bg-red-100 text-red-700',
  };

  const getDifferenceDisplay = (current?: number, original?: number) => {
    if (!showDifference || current === undefined || original === undefined) return null;
    
    const diff = current - original;
    const isIncrease = diff > 0;
    
    return (
      <span className={`ml-1 font-medium ${isIncrease ? 'text-red-600' : 'text-green-600'}`}>
        ({isIncrease ? '+' : ''}
        {formatCurrency(diff)})
      </span>
    );
  };

  return (
    <div className={`p-3 border rounded-md ${bgColors[variant]}`}>
      <div className="flex justify-between">
        <span className="font-medium">{item.description}</span>
        {item.section && (
          <Badge variant="outline" className={badgeColors[variant]}>
            {item.section}
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2 text-sm">
        <div>
          <span className="text-muted-foreground">Quantity:</span> {item.quantity}
          {showDifference && originalValue !== undefined && item.quantity !== undefined && (
            <span className={`ml-1 font-medium ${item.quantity > originalValue ? 'text-red-600' : 'text-green-600'}`}>
              ({item.quantity > originalValue ? '+' : ''}
              {item.quantity - originalValue})
            </span>
          )}
        </div>
        <div>
          <span className="text-muted-foreground">Price:</span> {formatCurrency(item.price)}
          {getDifferenceDisplay(item.price, originalValue)}
        </div>
        <div>
          <span className="text-muted-foreground">Total:</span> {formatCurrency(item.totalPrice)}
          {getDifferenceDisplay(item.totalPrice, originalValue)}
        </div>
        {item.laborHours && (
          <div>
            <span className="text-muted-foreground">Labor:</span> {item.laborHours} hrs
            {showDifference && originalValue !== undefined && (
              <span className={`ml-1 font-medium ${item.laborHours > originalValue ? 'text-red-600' : 'text-green-600'}`}>
                ({item.laborHours > originalValue ? '+' : ''}
                {(item.laborHours - originalValue).toFixed(1)})
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
